/* eslint-disable max-lines */
import { Center, Flex, Icon } from '@chakra-ui/react';
import { ListItemText } from 'app/components/ChakraTableList';
import { AudioPlayControls } from 'app/components/Song/Audio/AudioPlayControls';
import { useAudioPlayHook } from 'app/components/Song/Audio/AudioPlayControls/AudioPlayHook';
import { getFormattedDuration } from 'app/components/Song/Audio/AudioTableList/utils';
import { AudioTitle } from 'app/components/Song/Audio/AudioTitle';
import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { useAppDispatch } from 'app/store';
import { setQueueIndexAction } from 'app/store/AudioPlayer/slice';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import { useIsMobile } from 'app/utils/hooks';
import { MouseEvent, useCallback } from 'react';
import { ReactComponent as CreditsIcon } from 'resources/images/credits.svg';
import { ReactComponent as DownloadIcon } from 'resources/images/download.svg';
import { ReactComponent as LyricsIcon } from 'resources/images/lyrics.svg';
import { ReactComponent as NotesIcon } from 'resources/images/notes.svg';
import { ShareType } from '../../Shares/types';

interface ReadOnlyPlaylistAudioRowProps {
  audio: AudioPlayerAudioInstance;
  index: number;
  actionsCount: number;
  showDownload: boolean;
  showLyrics: boolean;
  showCredits: boolean;
  shareType?: ShareType;
  handleDownload: (playlistAudio: AudioPlayerAudioInstance) => void;
  handleClickLyrics: (playlistAudio: AudioPlayerAudioInstance) => Promise<void>;
  handleClickCredits: (
    playlistAudio: AudioPlayerAudioInstance
  ) => Promise<void>;
  setViewedNoteAudio: (
    value: React.SetStateAction<AudioPlayerAudioInstance | null>
  ) => void;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}

export const ReadOnlyPlaylistAudioRow = ({
  audio,
  index: rowIndex,
  actionsCount,
  setViewedNoteAudio,
  showDownload,
  showLyrics,
  showCredits,
  handleDownload,
  handleClickLyrics,
  handleClickCredits,
  contextHandler,
  shareType,
}: ReadOnlyPlaylistAudioRowProps) => {
  const isLoading = ({ isAudioLoading, audioPlayerGuid }) =>
    isAudioLoading && audio.playlistAudioGuid === audioPlayerGuid;
  const audioFileDuration = getFormattedDuration(audio.duration);

  const dispatch = useAppDispatch();
  const isMobile = useIsMobile(false);
  const setQueueIndex = useCallback(
    (index: number) => dispatch(setQueueIndexAction(index)),
    [dispatch]
  );

  const { audioGuid, parentGuid, songStatus, songTitle } = audio;
  const songDetails = { title: songTitle, status: songStatus! };
  const playlistAudioGuid = audio.playlistAudioGuid!;
  const handleAudioLoad = () => setQueueIndex(rowIndex);

  const audioPlay = useAudioPlayHook({
    audioGuid,
    parentGuid: parentGuid!,
    songDetails,
    handleAudioLoad,
    playlistAudioGuid,
  });

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      data-group
      onContextMenu={contextHandler}
      onDoubleClick={audioPlay.handlePlay}
    >
      <Td position="relative">
        {audio.audioGuid ? (
          <>
            <AudioPlayControls
              isLoading={isLoading}
              audioGuid={audioGuid}
              playlistAudioGuid={playlistAudioGuid}
              parentGuid={parentGuid!}
              songDetails={songDetails}
              handleAudioLoad={handleAudioLoad}
              sx={{
                zIndex: 1,
                visibility: audioPlay.isActive ? 'visible' : 'hidden',
                _groupHover: {
                  visibility: 'visible',
                },
              }}
            />
            {audioPlay.isActive ? (
              ''
            ) : (
              <Center position="absolute" top={0} lineHeight="48px" minW={5}>
                {rowIndex + 1}
              </Center>
            )}
          </>
        ) : (
          <Center>{rowIndex}</Center>
        )}
      </Td>
      <Td>
        <AudioTitle
          audioGuid={audioGuid}
          playlistAudioGuid={playlistAudioGuid}
          title={audio.audioTitle || ''}
          isLatestVersion={false}
        />
      </Td>
      {shareType === ShareType.PLAYLIST && (
        <Td>
          <ListItemText
            primary={audio.songTitle}
            title={audio.songTitle}
            data-testid="song-title"
          />
        </Td>
      )}
      <Td>
        {audio.note && (
          <>
            {isMobile ? (
              <Center
                h="full"
                m="auto"
                cursor="pointer"
                data-testid="open-audio-note-button"
                aria-label="audio note"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewedNoteAudio(audio);
                }}
              >
                <Icon
                  as={NotesIcon}
                  width={5}
                  height={5}
                  color="textSubdued"
                  _hover={{ color: 'M.70' }}
                />
              </Center>
            ) : (
              <ListItemText
                title={audio.note}
                primary={audio.note}
                data-testid="audio-note-button"
                primaryTextProps={{
                  cursor: 'pointer',
                  color: 'textSubdued',
                  maxWidth: '255px',
                  onClick: (e) => {
                    e.stopPropagation();
                    setViewedNoteAudio(audio);
                  },
                }}
              />
            )}
          </>
        )}
      </Td>
      <Td>
        <ListItemText
          primary={audioFileDuration || ''}
          data-testid="audio-duration"
        />
      </Td>
      {actionsCount > 0 && (
        <Td>
          <Flex gap={6} w="fit-content">
            {showDownload && (
              <Center
                title="Download song"
                h="full"
                m="auto"
                cursor="pointer"
                data-testid="download-audio-button"
                aria-label="download audio"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(audio);
                }}
              >
                <Icon
                  as={DownloadIcon}
                  _hover={{ color: 'M.70' }}
                  width={5}
                  height={5}
                  color="textSubdued"
                />
              </Center>
            )}
            {showLyrics && (
              <Center
                title="Show Lyrics"
                h="full"
                m="auto"
                cursor="pointer"
                data-testid="open-lyrics-button"
                aria-label="lyrics"
                onClick={async (e) => {
                  e.stopPropagation();
                  handleClickLyrics(audio);
                }}
              >
                <Icon
                  as={LyricsIcon}
                  _hover={{ color: 'M.70' }}
                  width={5}
                  height={5}
                  color="textSubdued"
                />
              </Center>
            )}
            {showCredits && (
              <Center
                title="Show Credits"
                h="full"
                m="auto"
                cursor="pointer"
                data-testid="open-credits-button"
                aria-label="credits"
                onClick={async (e) => {
                  e.stopPropagation();
                  handleClickCredits(audio);
                }}
              >
                <Icon
                  as={CreditsIcon}
                  _hover={{ color: 'M.70' }}
                  width={5}
                  height={5}
                  color="textSubdued"
                />
              </Center>
            )}
          </Flex>
        </Td>
      )}

      <Td className="gap" />
      {actionsCount > 0 && (
        <Td>
          <ContextMenuButton onClick={contextHandler} />
        </Td>
      )}
    </Tr>
  );
};
